import {DestroyRef, Injectable, NgZone} from '@angular/core';
import {SpatialUtilService} from '../services/spatial-util.service';
import {PrintAreaService} from '../../common/service/print-area.service';
import {LabelExpressionService} from '../services/label-expression/label-expression.service';
import {TenureService} from '../../plan-information/tenure/tenure.service';
import {MapModeService} from '../services/map-mode.service';
import {MapToolsService} from '../services/map-tools.service';
import {GraphicSelectionService} from '../services/graphic-selection.service';
import {EsriModuleService} from '../esri-nlr/services/esri-module.service';
import {isNumber} from '../../common/service/data-util';
import {ActivatedRoute, Router} from '@angular/router';
import {NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {auditTime} from 'rxjs';
import {tap} from 'rxjs/operators';
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
@Injectable({
    providedIn: 'root'
})
export class LayerUtilService {
    private setDisplayAttrsTimeout: number;

    constructor(
        private spatialUtilService: SpatialUtilService,
        private printAreaService: PrintAreaService,
        private labelExpressionService: LabelExpressionService,
        private nlrpTenureService: TenureService,
        private mapModeService: MapModeService,
        private mapToolsService: MapToolsService,
        private graphicSelectionService: GraphicSelectionService,
        private esriModuleService: EsriModuleService,
        private route: ActivatedRoute,
        private router: Router,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef,
        private ngZone: NgZone
    ) {
        const dojoxGfxSvg = (window as any).dojox.gfx.svg;
        if (dojoxGfxSvg) {
            const shapeSuperclassSetTranform = dojoxGfxSvg.Shape.superclass.setTransform;
            dojoxGfxSvg.Shape.superclass.setTransform = function (transform) {
                if (
                    this.parent &&
                    (this.parent.id === 'mainMap_annotationFeatureLayer' || this.parent.id === 'mainMap_angleFeatureLayer') &&
                    transform.dx === 0 &&
                    transform.dy === 0 &&
                    transform.xx === 1 &&
                    transform.xy === 0 &&
                    transform.yx === 0 &&
                    transform.yy === 1
                ) {
                    return this;
                }
                return shapeSuperclassSetTranform.call(this, transform);
            };
        }
        this.eventBus
            .on('featureHighlightsChanged')
            .pipe(
                auditTime(50),
                takeUntilDestroyed(this.destroyRef),
                tap(() => {
                    console.log('on featureHighlightChanges  layer-util ');
                })
            )
            .subscribe(() => {
                this.ngZone.runOutsideAngular(() => {
                    this.spatialUtilService.getFeatureClasses().forEach(featureClass => {
                        const featureIdAttrName = `data-${featureClass}-id`;
                        const featureList =
                            document.querySelectorAll(`[${featureIdAttrName}]`) || window.opener.document.querySelectorAll(`[${featureIdAttrName}]`);
                        featureList.forEach(node => {
                            const featureId = parseInt(node.getAttribute(featureIdAttrName));
                            this.setDisplayAttrs(node, featureClass, featureId);
                        });
                    });
                });
            });
    }

    private pointsToPixels = 96 / 72;

    private pointLayerId = 'pointFeatureLayer_layer';
    private pointLayerSelector = `#${this.pointLayerId}`;

    private layerEventMap = {};

    private esri = this.esriModuleService.getEsri();

    addLayerEventHandler(layer, eventName, eventHandler) {
        const featureClass = this.spatialUtilService.getFeatureClass(layer);

        if (!this.layerEventMap[featureClass]) {
            this.layerEventMap[featureClass] = {};
        }

        if (this.layerEventMap[featureClass][eventName]) {
            this.layerEventMap[featureClass][eventName].esriEventHandler.remove();
        }

        this.layerEventMap[featureClass][eventName] = {
            eventHandler: eventHandler,
            esriEventHandler: this.ngZone.runOutsideAngular(() => layer.on(eventName, eventHandler))
        };
    }

    getLayerEventHandler(featureClassOrLayer, eventName) {
        const featureClass = this.spatialUtilService.getFeatureClass(featureClassOrLayer);
        return this.layerEventMap[featureClass][eventName].eventHandler;
    }

    getPointOffset(textX, textY, textAnchor, feature, featureType) {
        const pointOffSetProperties = {x: 0, y: 0};

        const map = this.spatialUtilService.getMap();
        if (!map) {
            return pointOffSetProperties;
        }

        const mapScale = map.getScale();
        if (!mapScale) {
            return pointOffSetProperties;
        }

        const enlargement = this.spatialUtilService.getEnlargements().find(f => f.attributes.id === this.mapModeService.mapMode.id);
        const planScale =
            enlargement && this.mapModeService.mapMode.type === 'enlargement'
                ? enlargement.attributes.scale
                : this.printAreaService.getReferenceScaleDivisor();

        if (Math.round(mapScale) === planScale) {
            pointOffSetProperties.x = textX;
            pointOffSetProperties.y = textY;
            return pointOffSetProperties;
        }

        if (this.mapToolsService.isScaleActive()) {
            pointOffSetProperties.x = textX;
            pointOffSetProperties.y = textY;
        } else {
            const annotationFeatureType = featureType || feature.attributes.featuretype;

            switch (annotationFeatureType) {
                case 'point':
                    this.getAdjustedPointTextGeometry(
                        textX,
                        textY,
                        feature,
                        this.spatialUtilService.getPoints().find(f => f.attributes.id === feature.attributes.featureid) ||
                            this.spatialUtilService.getControlPoints().find(f => f.attributes.id === feature.attributes.featureid),
                        mapScale,
                        planScale,
                        pointOffSetProperties
                    );
                    break;
                case 'angle':
                    this.getAdjustedPointTextGeometry(
                        textX,
                        textY,
                        feature,
                        this.spatialUtilService.getPoints().find(f => f.attributes.id === feature.attributes.pointid) ||
                            this.spatialUtilService.getControlPoints().find(f => f.attributes.id === feature.attributes.pointid),
                        mapScale,
                        planScale,
                        pointOffSetProperties
                    );
                    break;
                case 'constructLine':
                    this.getAdjustedLineTextGeometry(
                        textX,
                        textY,
                        feature,
                        this.spatialUtilService.getConstructLines().find(f => f.attributes.id === feature.attributes.featureid),
                        mapScale,
                        planScale,
                        pointOffSetProperties
                    );
                    break;
                case 'line':
                    this.getAdjustedLineTextGeometry(
                        textX,
                        textY,
                        feature,
                        this.spatialUtilService.getLines().find(f => f.attributes.id === feature.attributes.featureid),
                        mapScale,
                        planScale,
                        pointOffSetProperties
                    );
                    break;
                case 'lot':
                case 'road':
                case 'general': {
                    const shape = feature.getShape().shape;
                    pointOffSetProperties.x = shape.cx;
                    pointOffSetProperties.y = shape.cy;
                    break;
                }
                default:
                    pointOffSetProperties.x = textX;
                    pointOffSetProperties.y = textY;
                    break;
            }
        }

        return pointOffSetProperties;
    }

    toMapPoint(screenX, screenY) {
        const map = this.spatialUtilService.getMap();
        return map.toMap(new this.esri.geometry.ScreenPoint(screenX, screenY));
    }

    toScreenPoint(mapX, mapY) {
        const map = this.spatialUtilService.getMap();
        return map.toScreen(new this.esri.geometry.Point(mapX, mapY));
    }

    getPolylinePathLengthInfo(path, spatialReference) {
        const pathLengthInfo = path
            .map(v => new this.esri.geometry.Point(v[0], v[1], spatialReference))
            .reduce(
                (acc, v, i, a) => {
                    const lastSegmentLength = i > 0 ? this.esri.geometry.getLength(a[i - 1], v) : 0.0;
                    const cumulativeLength = acc.totalLength + lastSegmentLength;
                    acc.cumulativeLengths.push(cumulativeLength);
                    acc.totalLength = cumulativeLength;
                    return acc;
                },
                {totalLength: 0, cumulativeLengths: []}
            );

        pathLengthInfo.halfLength = pathLengthInfo.totalLength / 2.0;
        pathLengthInfo.middleSegmentIndex = pathLengthInfo.cumulativeLengths.findIndex(
            (v, i, a) => i < a.length - 1 && a[i] < pathLengthInfo.halfLength && a[i + 1] > pathLengthInfo.halfLength
        );

        return pathLengthInfo;
    }

    getAdjustedLineTextGeometry(textX, textY, annotation, line, mapScale, planScale, pointOffSetProperties) {
        const scaleRatio = mapScale / planScale;

        // find the point on the line where a line from the annotation would meet at 90deg
        let path = line.geometry.paths[0];
        if (path.length > 2) {
            //If it's a polyline, use the middle path segment
            const pathLengthInfo = this.getPolylinePathLengthInfo(path, line.geometry.spatialReference);
            path = path.slice(pathLengthInfo.middleSegmentIndex, pathLengthInfo.middleSegmentIndex + 2);
        }

        const lineRadians = Math.atan2(path[1][1] - path[0][1], path[1][0] - path[0][0]);
        const featureRadians = Math.atan2(annotation.geometry.y - path[0][1], annotation.geometry.x - path[0][0]);
        const hypotenuseDistance = Math.sqrt(Math.pow(annotation.geometry.x - path[0][0], 2) + Math.pow(annotation.geometry.y - path[0][1], 2));
        const angle = Math.atan2(Math.sin(featureRadians - lineRadians), Math.cos(featureRadians - lineRadians));
        const intersectDistance = hypotenuseDistance * Math.cos(angle);
        const intersectX = path[0][0] + intersectDistance * Math.cos(lineRadians);
        const intersectY = path[0][1] + intersectDistance * Math.sin(lineRadians);
        const intersectRadians = Math.atan2(annotation.geometry.y - intersectY, annotation.geometry.x - intersectX);
        const distanceFromLine = hypotenuseDistance * Math.sin(angle);

        const scaledX = intersectX + distanceFromLine * scaleRatio * Math.cos(intersectRadians) * (angle < 0 ? -1 : 1);
        const scaledY = intersectY + distanceFromLine * scaleRatio * Math.sin(intersectRadians) * (angle < 0 ? -1 : 1);

        const unscaledX = intersectX + distanceFromLine * Math.cos(intersectRadians) * (angle < 0 ? -1 : 1);
        const unscaledY = intersectY + distanceFromLine * Math.sin(intersectRadians) * (angle < 0 ? -1 : 1);

        const screenPointScaled = this.toScreenPoint(scaledX, scaledY);
        const screenPointUnscaled = this.toScreenPoint(unscaledX, unscaledY);

        pointOffSetProperties.x = textX + screenPointScaled.x - screenPointUnscaled.x;
        pointOffSetProperties.y = textY + screenPointScaled.y - screenPointUnscaled.y;
    }

    getAdjustedPointTextGeometry(textX, textY, angleOrAnnotation, relatedPoint, mapScale, planScale, pointOffSetProperties) {
        const scaleRatio = mapScale / planScale;

        const angleOrAnnotationScreenPoint = this.toScreenPoint(angleOrAnnotation.geometry.x, angleOrAnnotation.geometry.y);

        const relatedPointScreenPoint = this.toScreenPoint(relatedPoint.geometry.x, relatedPoint.geometry.y);

        // scale the distance between the two points according to the change in map scale
        const distance = Math.sqrt(
            Math.pow(angleOrAnnotationScreenPoint.x - relatedPointScreenPoint.x, 2) +
                Math.pow(angleOrAnnotationScreenPoint.y - relatedPointScreenPoint.y, 2)
        );

        const bearing = Math.atan2(
            angleOrAnnotationScreenPoint.y - relatedPointScreenPoint.y,
            angleOrAnnotationScreenPoint.x - relatedPointScreenPoint.x
        );

        pointOffSetProperties.x = textX + distance * Math.cos(bearing) * (scaleRatio - 1);
        pointOffSetProperties.y = textY + distance * Math.sin(bearing) * (scaleRatio - 1);

        return pointOffSetProperties;
    }

    calcBearing(x1, y1, x2, y2) {
        const azimuth = Math.atan2(x2 - x1, y2 - y1);
        const azimuthInDegrees = (azimuth * 180) / Math.PI;
        let fixAzimuth = azimuthInDegrees % 360;

        if (fixAzimuth < 0) {
            fixAzimuth += 360;
        }

        return fixAzimuth;
    }

    getScaleFactor() {
        const map = this.spatialUtilService.getMap();
        if (!map) {
            return 0;
        }

        const mapScale = map.getScale();
        if (!mapScale) {
            return 0;
        }

        const mapMode = this.spatialUtilService.getMapMode();
        if (!mapMode) {
            return 0;
        }

        const plotScale =
            mapMode.type === 'mainPlan' ? this.printAreaService.getReferenceScaleDivisor() : mapMode.enlargementFeature.attributes.scale;
        if (!plotScale) {
            return 0;
        }

        return this.mapToolsService.isScaleActive() ? plotScale / mapScale : 1.0;
    }

    getPixelScaleFactor() {
        return this.pointsToPixels * this.getScaleFactor();
    }

    getPointScaleFactor() {
        return this.getScaleFactor();
    }

    getStrokeWidthPixelScaleFactor(pixelScaleFactor) {
        return Math.max(this.pointsToPixels, pixelScaleFactor);
    }

    setAngleLabelProperties(graphic) {
        const res = this.labelExpressionService.create(graphic.attributes);
        if (res) {
            graphic.attributes.labelproperties = JSON.stringify(res.labels);
        }
    }

    setAnnotationLabelProperties(graphic) {
        let polygons;

        if (graphic.attributes.featuretype === 'lot') {
            const lot = this.spatialUtilService.getLotGraphics().find(l => l.attributes.id === graphic.attributes.featureid);
            if (lot) {
                polygons = this.spatialUtilService.getPolygonGraphics().filter(p => lot.attributes.polygonids.includes(`[${p.attributes.id}]`));
            }
        }

        this.getCustomAttributes(graphic);

        const res = this.labelExpressionService.create(graphic.attributes, polygons);
        if (res) {
            graphic.attributes.labelproperties = JSON.stringify(res.labels);
            graphic.attributes.labelexpression = res.labelExpression;
        }
    }

    getCustomAttributes(graphic) {
        if ([2, 24, 25].includes(graphic.attributes.featuresubtype) && graphic.attributes.featuretype === 'general') {
            //this category can be added to lookup to deal with this graphic merging

            const count = graphic.attributes.attribute1.split('[').length;
            let attribute1 = graphic.attributes.attribute1;

            if (graphic.attributes.attribute2) {
                attribute1 = attribute1.replace(/\[(.+?)\]/, graphic.attributes.attribute2);
            }
            attribute1 = attribute1.replace(/( METRES )/, '$1 \n');

            if (count > 2) {
                if (graphic.attributes.attribute3) {
                    if (graphic.attributes.featuresubtype === 24) {
                        graphic.attributes.attribute3 = this.nlrpTenureService.sanitiseRangesText(graphic.attributes.attribute3);
                    }
                    attribute1 = attribute1.replace(/\[(.+?)\]/, graphic.attributes.attribute3);
                } else {
                    //TODO: Can be done in graphic generic way
                    if (graphic.attributes.featuresubtype === 25) {
                        const expr = graphic.attributes.labelexpression.match('AS TO(.*)ONLY');
                        if (expr.length === 2) {
                            attribute1 = attribute1.replace(/\[(.+?)\]/, expr[1]);
                        }
                    }
                }
            }
            graphic.attributes['attributeCustom'] = attribute1;
        }
    }

    getSvgRotation(bearing) {
        bearing = (bearing + 360) % 360;

        if (bearing === 0) {
            return 90;
        }

        if (bearing > 0 && bearing < 1) {
            return 90;
        }

        if (bearing >= 1 && bearing < 90) {
            return bearing + 270;
        }

        if (bearing === 90) {
            return 0;
        }

        if (bearing > 90 && bearing < 180) {
            return bearing - 90;
        }

        if (bearing === 180) {
            return 90;
        }

        if (bearing === 181) {
            return 270;
        }

        if (bearing > 180 && bearing <= 181) {
            return 90;
        }

        if (bearing > 181 && bearing < 270) {
            return bearing + 90;
        }

        if (bearing === 270) {
            return 0;
        }

        if (bearing >= 359) {
            return 90;
        }

        if (bearing > 270) {
            return bearing - 270;
        }
    }

    getFeatureIdAttrName(featureClass) {
        return `data-${featureClass}-id`;
    }

    getFeatureTypeAttrName(featureClass) {
        return `data-${featureClass}-type`;
    }

    setSelectedAttr(node, featureClass, featureId) {
        if (this.spatialUtilService.isHighlightedFeature(featureClass, featureId)) {
            node.setAttribute('data-selected', '');
        } else {
            node.removeAttribute('data-selected');
        }
    }

    setHiddenAttr(node, featureClass, featureId) {
        if (this.spatialUtilService.isFeatureHidden(featureClass, featureId)) {
            node.setAttribute('data-hidden', '');
        } else {
            node.removeAttribute('data-hidden');
        }
    }

    setDisplayAttrs(node, featureClass, featureId) {
        this.setSelectedAttr(node, featureClass, featureId);
        this.setHiddenAttr(node, featureClass, featureId);
    }

    getFeatureNodeId(featureClass, featureId) {
        return `${featureClass}-feature-${featureId}`;
    }

    onMouseOver(featureClass, evt) {
        this.ngZone.runOutsideAngular(() => {
            const featureId = evt.graphic.attributes.id;
            const featureNodeId = this.getFeatureNodeId(featureClass, featureId);
            const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
            use.setAttribute('href', `#${featureNodeId}`);
            use.setAttribute('pointer-events', 'none');
            document.getElementById('mainMap_gc').appendChild(use);
        });
    }

    onMouseOut(featureClass, evt) {
        this.ngZone.runOutsideAngular(() => {
            const featureId = evt.graphic.attributes.id;
            const featureNodeId = this.getFeatureNodeId(featureClass, featureId);
            const use = document.querySelector(`#mainMap_gc > use[href="#${featureNodeId}"]`);
            if (use) {
                use.parentNode.removeChild(use);
            }
        });
    }

    onClick(featureClass, evt) {
        this.ngZone.runOutsideAngular(() => {
            if (
                evt.which !== 1 ||
                !this.mapToolsService.isActive('MAP_SELECTION_CLICK') ||
                this.graphicSelectionService.applyFeaturesFilter(featureClass, [evt.graphic]).length === 0
            ) {
                return;
            }

            if (evt.target.hasAttribute('data-selected')) {
                if (evt.ctrlKey || evt.shiftKey) {
                    evt.target.removeAttribute('data-selected');
                }
            } else {
                evt.target.setAttribute('data-selected', '');
            }
        });
    }

    private hoverColor = 'rgb(255,0,0)';
    private selectedColor = 'rgb(0,255,255)';
    private hiddenColor = 'magenta';

    getHoverColor() {
        return this.hoverColor;
    }

    getSelectedColor() {
        return this.selectedColor;
    }

    getHiddenColor() {
        return this.showFeatures() ? this.hiddenColor : 'transparent';
    }

    getGraphicShape(graphic) {
        return graphic.getShape().shape;
    }

    getScreenCoords(x, y, ctm) {
        return {
            x: ctm.e + x * ctm.a + y * ctm.c,
            y: ctm.f + x * ctm.b + y * ctm.d
        };
    }

    getLayerNodeId(featureClass) {
        switch (featureClass) {
            case 'affectedlot':
                return 'annotationFeatureLayer_layer';
            case 'angle':
                return 'angleFeatureLayer_layer';
            case 'annotation':
                return 'annotationFeatureLayer_layer';
            case 'clipping':
                return 'clippingFeatureLayer_layer';
            case 'constructline':
                return 'constructLineFeatureLayer_layer';
            case 'constructpoint':
                return 'constructPointFeatureLayer_layer';
            case 'controlpoint':
                return 'controlPointFeatureLayer_layer';
            case 'enlargement':
                return 'enlargementFeatureLayer_layer';
            case 'interest':
                return 'interestFeatureLayer_layer';
            case 'line':
                return 'lineFeatureLayer_layer';
            case 'lot':
                return 'lotFeatureLayer_layer';
            case 'point':
                return 'pointFeatureLayer_layer';
            case 'polygon':
                return 'polygonFeatureLayer_layer';
            case 'polyline':
                return 'polylineFeatureLayer_layer';
            case 'printarea':
                return 'printAreaFeatureLayer_layer';
            case 'road':
                return 'roadFeatureLayer_layer';
        }
        return undefined;
    }

    getLayerNodeSelector(featureClass) {
        return `#${this.getLayerNodeId(featureClass)}`;
    }

    getLayerNode(featureClass) {
        return document.getElementById(this.getLayerNodeId(featureClass));
    }

    getFeatureDisplayNodeSelector(featureClass, featureId) {
        let tagName = 'g';

        switch (featureClass) {
            case 'angle':
                tagName = 'g';
                break;
            case 'annotation':
                tagName = 'g';
                break;
            case 'clipping':
                tagName = 'g';
                break;
            case 'constructline':
                tagName = 'path';
                break;
            case 'constructpoint':
                tagName = 'text';
                break;
            case 'controlpoint':
                tagName = 'text';
                break;
            case 'enlargement':
                tagName = 'g';
                break;
            case 'interest':
                tagName = 'g';
                break;
            case 'line':
                tagName = 'path';
                break;
            case 'lot':
                tagName = 'g';
                break;
            case 'point':
                tagName = 'text';
                break;
            case 'polygon':
                tagName = 'g';
                break;
            case 'polyline':
                tagName = 'path';
                break;
            case 'printarea':
                tagName = 'g';
                break;
            case 'road':
                tagName = 'g';
                break;
        }

        const featureIdAttrName = this.getFeatureIdAttrName(featureClass);
        const featureSelector = `${tagName}[${featureIdAttrName}="${featureId}"]`;
        return `${this.getLayerNodeSelector(featureClass)} > ${featureSelector}`;
    }

    getFeatureDisplayNode(featureClass, featureId) {
        return document.querySelector(this.getFeatureDisplayNodeSelector(featureClass, featureId));
    }

    getFeatureCornerScreenCoords(featureClass, featureId) {
        const featureNode = this.getFeatureDisplayNode(featureClass, featureId) as SVGGraphicsElement;
        if (featureNode) {
            const bbox = featureNode.getBBox();
            const ctm = featureNode.getCTM();
            const tl = this.getScreenCoords(bbox.x, bbox.y, ctm);
            const tr = this.getScreenCoords(bbox.x + bbox.width, bbox.y, ctm);
            const br = this.getScreenCoords(bbox.x + bbox.width, bbox.y + bbox.height, ctm);
            const bl = this.getScreenCoords(bbox.x, bbox.y + bbox.height, ctm);
            return [tl, tr, br, bl];
        }
        return undefined;
    }

    getMainMapSvgOffsetXY() {
        const mainMapSvgNode = document.querySelector('#mainMap_gc') as SVGGraphicsElement;
        const mainMapRect = mainMapSvgNode.getBoundingClientRect();
        const mainMapParentRect = mainMapSvgNode.parentElement.getBoundingClientRect();
        return [mainMapRect.x - mainMapParentRect.x, mainMapRect.y - mainMapParentRect.y];
    }

    getFeatureCornerMapCoords(featureClass, featureId) {
        const offsetXY = this.getMainMapSvgOffsetXY();

        const map = this.spatialUtilService.getMap();
        const screenCoords = this.getFeatureCornerScreenCoords(featureClass, featureId);
        if (screenCoords) {
            return screenCoords.map(v => {
                v.x += offsetXY[0];
                v.y += offsetXY[1];

                const mapPoint = map.toMap(new this.esri.geometry.ScreenPoint(v));
                return [mapPoint.x, mapPoint.y];
            });
        }
        return undefined;
    }

    getFeatureBoundingBox(featureClass, featureId) {
        const cornerMapCoords = this.getFeatureCornerMapCoords(featureClass, featureId);
        if (cornerMapCoords) {
            return cornerMapCoords.reduce(
                (acc, v) => {
                    if (v[0] < acc.xmin) acc.xmin = v[0];
                    if (v[1] < acc.ymin) acc.ymin = v[1];
                    if (v[0] > acc.xmax) acc.xmax = v[0];
                    if (v[1] > acc.ymax) acc.ymax = v[1];
                    return acc;
                },
                {xmin: Number.MAX_VALUE, ymin: Number.MAX_VALUE, xmax: -Number.MAX_VALUE, ymax: -Number.MAX_VALUE}
            );
        }
        return undefined;
    }

    getFeatureBoundingGeometry(featureClass, featureOrFeatureId) {
        const featureId = isNumber(featureOrFeatureId) ? featureOrFeatureId : featureOrFeatureId.attributes.id;
        const mapCoords = this.getFeatureCornerMapCoords(featureClass, featureId);
        if (mapCoords) {
            return new this.esri.geometry.Polygon(mapCoords);
        }
    }

    getSpatialQueryFeatureGeometry(featureClassOrLayer, feature) {
        const featureClass = this.spatialUtilService.getFeatureClass(featureClassOrLayer);
        return (
            (['angle', 'annotation', 'constructpoint', 'controlpoint', 'point'].includes(featureClass) &&
                this.getFeatureBoundingGeometry(featureClass, feature)) ||
            this.getOffsetGeometry(this.spatialUtilService.getMap(), featureClass, feature)
        );
    }

    // Set graphic extent to display node extent to stop annotations and angles disappearing when their associated point geometry moves out of view
    setGraphicExtentToDisplayNodeExtent(featureClass, graphic) {
        const boundingBox = this.getFeatureBoundingBox(featureClass, graphic.attributes.id);
        if (boundingBox) {
            graphic._extent.xmin = boundingBox.xmin;
            graphic._extent.ymin = boundingBox.ymin;
            graphic._extent.xmax = boundingBox.xmax;
            graphic._extent.ymax = boundingBox.ymax;
        }
    }

    suspendDragDrawTools(featureClass, featureId) {
        if (
            this.mapToolsService.isMoveActive() ||
            this.mapToolsService.isRotateActive() ||
            this.spatialUtilService.isHighlightedFeature(featureClass, featureId)
        ) {
            this.mapToolsService.suspend('MAP_SELECTION_DRAG');
            this.mapToolsService.suspend('ZOOM');
        }
    }

    restoreDragDrawTools() {
        if (this.mapToolsService.isMoveActive() || this.mapToolsService.isRotateActive()) {
            this.mapToolsService.restore('MAP_SELECTION_DRAG', {redraw: false});
            this.mapToolsService.restore('ZOOM');
        }
    }

    showFeatures() {
        return (
            !this.mapToolsService.isActive('DISPLAY_MODE_PRINT_PREVIEW') ||
            (this.router.url.includes('plan') && this.route.snapshot.queryParamMap.get('printPreview') === 'client')
        );
    }

    private covenantLineType = 24;
    private totalLotBoundaryLineType = 95;
    private polylineTypes = [this.covenantLineType, this.totalLotBoundaryLineType];

    // Lines must be easily selectable when zoomed out, so the stroke width of the line that captures the pointer events must be sufficiently large
    getPointerEventsLineWidthPixelScaleFactor(pixelScaleFactor) {
        return Math.max(15, this.getStrokeWidthPixelScaleFactor(pixelScaleFactor));
    }

    getOffsetGeometry(map, featureClassOrLayer, feature) {
        let geometry = feature.geometry;

        const featureClass = this.spatialUtilService.getFeatureClass(featureClassOrLayer);

        if (['line', 'polyline'].includes(featureClass) && this.polylineTypes.includes(feature.attributes.type)) {
            const offsetScaleFactor = (this.getStrokeWidthPixelScaleFactor(this.getPixelScaleFactor()) * map.extent.getWidth()) / map.width;
            switch (feature.attributes.type) {
                case this.totalLotBoundaryLineType:
                    geometry = this.esriModuleService.geometryEngine.offset(geometry, 3.2 * offsetScaleFactor, 'meters', 'round');
                    break;
                case this.covenantLineType:
                    geometry = this.esriModuleService.geometryEngine.offset(geometry, 1.6 * offsetScaleFactor, 'meters', 'round');
                    break;
            }
        }

        return geometry;
    }

    monkeyPatchFeatureLayerDrawShapeForPolylines(featureLayer) {
        featureLayer._drawShape = (graphic, b, c, d) => {
            if (!graphic.attributes) {
                return featureLayer.prototype._drawShape.call(this, graphic, b, c, d);
            }

            const map = featureLayer._map;

            const geom = this.getOffsetGeometry(map, featureLayer, graphic);

            let k = 0;
            let h = [];
            for (let l = b.length; k < l; k++) {
                h = h.concat(
                    this.esri.geometry._toScreenPath(map.extent, map.width, map.height, geom, -map.__visibleRect.x + b[k], -map.__visibleRect.y)
                );
            }

            d = featureLayer._drawPath(c, d, h);
            featureLayer._rendererLimits && featureLayer._clipPolyline(d, geom);
            return d;
        };
    }
}
